import { Component, Inject, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '../../../../core/service/auth.service';
import { ResetPasswordRequest } from '../../../../core/models/reset-password-request.model';

@Component({
  selector: 'app-reset-password-dialog',
  standalone: false,
  template: `
    <div class="bg-white rounded-2xl overflow-hidden flex flex-col max-w-md w-full">
      
      <div class="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
        <h2 class="text-xl font-bold text-slate-800 flex items-center gap-2">
          <lucide-icon name="key" class="w-5 h-5 text-amber-500"></lucide-icon>
          Смяна на парола
        </h2>
        <button (click)="dialogRef.close()" class="text-slate-400 hover:text-slate-600 transition-colors">
          <lucide-icon name="x" class="w-5 h-5"></lucide-icon>
        </button>
      </div>

      <div class="p-6">
        <div class="mb-4 p-3 bg-amber-50 border border-amber-100 rounded-xl flex gap-3 text-sm text-amber-800">
           <lucide-icon name="alert-circle" class="w-5 h-5 shrink-0"></lucide-icon>
           <p>Вие променяте паролата за потребител <strong>{{ data.username }}</strong>. Старата парола ще стане невалидна веднага.</p>
        </div>

        <form [formGroup]="form" (ngSubmit)="onSubmit()" class="space-y-4">
          
          <div class="space-y-1.5">
            <label class="text-sm font-medium text-slate-700">Нова Парола *</label>
            <div class="relative">
              <input [type]="showPassword ? 'text' : 'password'" 
                     formControlName="newPassword"
                     class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all pr-10"
                     [class.border-red-500]="(f['newPassword'].invalid && f['newPassword'].touched) || (passwordErrors.length > 0 && f['newPassword'].dirty)"
                     placeholder="******">
              
              <button type="button" (click)="showPassword = !showPassword" 
                      class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <lucide-icon [name]="showPassword ? 'eye-off' : 'eye'" class="w-4 h-4"></lucide-icon>
              </button>
            </div>
            @if (f['newPassword'].hasError('required') && f['newPassword'].touched) {
               <p class="text-xs text-red-500">Полето е задължително.</p>
            }
          </div>

          <div class="space-y-1.5">
            <label class="text-sm font-medium text-slate-700">Повтори Новата Парола *</label>
            <div class="relative">
              <input [type]="showPassword ? 'text' : 'password'" 
                     formControlName="newPasswordConfirm"
                     class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all pr-10"
                     [class.border-red-500]="(f['newPasswordConfirm'].invalid && f['newPasswordConfirm'].touched) || (form.errors?.['mismatch'] && f['newPasswordConfirm'].touched)"
                     placeholder="******">
            </div>
            @if (form.errors?.['mismatch'] && f['newPasswordConfirm'].touched) {
               <p class="text-xs text-red-500">Паролите не съвпадат.</p>
            }
          </div>

          @if (passwordErrors.length > 0 && f['newPassword'].dirty) {
            <div class="bg-red-50 border border-red-200 rounded-xl p-3">
              <p class="font-semibold text-red-900 mb-2 text-xs">Изисквания за паролата:</p>
              <ul class="list-disc list-inside space-y-1 text-xs text-red-700">
                @for (error of passwordErrors; track error) {
                  <li>{{ error }}</li>
                }
              </ul>
            </div>
          }

          <div class="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <p class="font-semibold text-blue-900 mb-2 text-xs">Паролата трябва да съдържа:</p>
            <ul class="list-disc list-inside space-y-1 text-xs text-blue-700">
              <li>Поне 8 символа</li>
              <li>Главни и малки букви</li>
              <li>Цифри (0-9)</li>
              <li>Специални символи (~!&#64;#$%^&*_-+=|(){{ '{' }}]{{ '}' }}:;"'<>,.?/)</li>
            </ul>
          </div>

        </form>
      </div>

      <div class="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
        <button type="button" (click)="dialogRef.close()" 
                class="px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-white border border-transparent hover:border-slate-200 rounded-xl transition-all">
          Отказ
        </button>
        
        <button type="button" (click)="onSubmit()" 
                [disabled]="form.invalid || isLoading || passwordErrors.length > 0"
                class="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 rounded-xl shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
          @if (isLoading) {
            <lucide-icon name="loader-2" class="w-4 h-4 mr-2 animate-spin"></lucide-icon>
          }
          Запази Паролата
        </button>
      </div>
    </div>
  `
})
export class ResetPasswordDialogComponent {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  public dialogRef = inject(MatDialogRef<ResetPasswordDialogComponent>);
  public data: { userId: number; username: string } = inject(MAT_DIALOG_DATA);

  form: FormGroup;
  isLoading = false;
  showPassword = false;

  constructor() {
    this.form = this.fb.group({
      newPassword: ['', [Validators.required]], 
      newPasswordConfirm: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  get f() { return this.form.controls; }

  get passwordErrors(): string[] {
    const password = this.form.get('newPassword')?.value || '';
    
    if (!password) {
      return [];
    }

    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Паролата трябва да е поне 8 символа');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Трябва да съдържа поне една главна буква');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Трябва да съдържа поне една малка буква');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Трябва да съдържа поне една цифра');
    }
    if (!/[~!@#$%^&*_\-+=`|(){}[\]:;"'<>,.?/]/.test(password)) {
      errors.push('Трябва да съдържа поне един специален символ');
    }
    
    return errors;
  }

  passwordMatchValidator(control: AbstractControl) {
    const password = control.get('newPassword')?.value;
    const confirm = control.get('newPasswordConfirm')?.value;
    if (!password || !confirm) return null;
    return password === confirm ? null : { mismatch: true };
  }

  onSubmit() {
    if (this.form.invalid || this.passwordErrors.length > 0) return;

    this.isLoading = true;
    
    const request: ResetPasswordRequest = {
      userId: this.data.userId,
      newPassword: this.form.value.newPassword
    };

    this.authService.resetPassword(request).subscribe({
      next: () => {
        this.dialogRef.close(true);
      },
      error: (err) => {
        console.error('Reset password failed', err);
        this.isLoading = false;
      }
    });
  }
}